import React, { useState, useEffect } from "react";
import { makeStyles, Button, Typography } from "@material-ui/core";
import { TextField } from "formik-material-ui";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import useCart from "../../hooks/useCart";
import { formatPriceCents } from "../../components/product/utils";
import { getCouponDiscount } from "../../lib/cart";
import { getCartPrice, getCurrentDiscount } from "../funnel/utils";
import Check from "./assets/check.svg";

import { getReferralCode } from "lib/referral";

const CouponSchema = Yup.object().shape({
  coupon: Yup.string().min(1, "Le code promo est trop court").max(50, "Le code promo est trop long"),
});

const CouponForm = () => {
  const [cart, dispatch] = useCart();
  const [error, setError] = useState("");
  const [couponValidated, setCouponValidity] = useState(false);
  const currentCartAmount = getCartPrice(cart);
  const classes = useStyle();

  useEffect(() => {
    setCouponValidity(cart.coupon.code.length > 0);
  }, [cart.coupon.code]);

  const removeCoupon = () => {
    dispatch({ type: "removeCoupon" });
    setError("");
  };

  return (
    <div className={classes.container__coupon}>
      {couponValidated ? (
        <div className={classes.success}>
          <div>
            <Typography className={classes.row} variant="body2">
              {" "}
              Code promo appliqué <Check />{" "}
            </Typography>
            <Typography className={classes.success__discount} variant="caption">
              {cart.coupon.code} {`-${formatPriceCents(getCurrentDiscount(cart))}`}
            </Typography>
          </div>
          <Button className={classes.cta__coupon} onClick={() => removeCoupon()}>
            Annuler
          </Button>
        </div>
      ) : (
        <Formik
          initialValues={{
            coupon: "",
          }}
          validationSchema={CouponSchema}
          onSubmit={async ({ coupon }, { setSubmitting }) => {
            setSubmitting(false);

            const data = await getCouponDiscount(coupon, currentCartAmount);
            if (data.error) {
              // If coupon was not found, try it with the referral endpoint
              if (data.code === "0008") {
                const referral = await getReferralCode(coupon);
                if (referral.status === 200) {
                  setError("Ce bon de parrainage n'est valable que pour la réparation à domicile !");
                  return;
                }
              }

              // Generic coupon error handling
              setError(data.message);
              return;
            }
            setCouponValidity(true);
            dispatch({ type: "addCoupon", coupon: data });
          }}
        >
          {({ submitForm, isSubmitting, isValid, values }) => (
            <Form>
              <div className={classes.inline}>
                <Field
                  component={TextField}
                  name="coupon"
                  type="text"
                  size="small"
                  className={classes.cta__input}
                  variant="outlined"
                  placeholder="Un code promo ?"
                  InputProps={{
                    classes: {
                      input: classes.label,
                    },
                  }}
                />
                <br />
                <Button
                  className={classes.cta__coupon}
                  disabled={!isValid || isSubmitting || values.coupon.length < 2}
                  onClick={submitForm}
                >
                  Ajouter
                </Button>
              </div>
              <small className={classes.serverError}>{error}</small>
            </Form>
          )}
        </Formik>
      )}
    </div>
  );
};

const useStyle = makeStyles((theme) => ({
  inline: {
    display: "flex",
    CenterItems: "center",
    "& > *": {
      marginRight: theme.spacing(4),
    },
  },
  row: {
    display: "flex",
    alignItems: "center",
  },
  container__coupon: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    background: "#F2F2F2",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
  },
  cta__input: {
    background: "white",
    height: 48,
    "& .MuiOutlinedInput-root": {
      height: 48,
      "& fieldset": {
        borderColor: theme.palette.inputBorder,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.text.disabled,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.text.disabled,
        border: "1px solid",
      },
    },
  },
  cta__coupon: {
    color: theme.palette.text.turquoise,
    paddingRight: 0,
    fontSize: 18,
    marginRight: 0,
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&:disabled": {
      color: theme.palette.text.turquoise,
    },
  },
  label: {
    fontSize: 17,
  },
  serverError: {
    display: "flex",
    justifyContent: "flex-start",
    color: "red",
    position: "relative",
  },
  success: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
  },
  success__discount: {
    color: theme.palette.text.success,
  },
}));
export default CouponForm;
